import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import useProducts from '../../../../hooks/useProducts';
import { Product } from '../../../../types/Products/Products';
import { NavForward } from '../../../../components/buttons/NavForward';
import { ProductCard } from '../../../../components/products/ProductCard';
import { Button } from '../../../../components';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { handleProductBasedNavUpdate } from '../../../../data/navigation/utilities/handleProductBasedNavUpdate';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/$patientId/package/select')({
  component: ProductSelectionPage,
});

function ProductSelectionPage() {
  const { mutate } = useUpdateChartSection();
  const [selected, setSelected] = React.useState<string>('');
  const [productConfirmed, setProductConfirmed] = React.useState<boolean>(false);
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const { isFetched: chartIsFetched } = chartQuery;
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const productsDocQuery = useProducts();
  const products = productsDocQuery.data as Product[];

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'select', patientInfo, value: true });
    }
  }, [patientInfo]);

  // SET SELECTED PRODUCT
  React.useEffect(() => {
    if (products && chart) {
      switch (chart?.PRODUCT_INFO?.selectedProduct) {
        case 'essential':
          setSelected('Self Guided Package');
          break;
        case 'vital':
          setSelected('Vital Package');
          break;
        case 'vip':
          setSelected('Physician Guided Package');
          break;
        case 'Self Guided Package':
          setSelected('Self Guided Package');
          break;
        case 'Vital Package':
          setSelected('Vital Package');
          break;
        case 'Physician Guided Package':
          setSelected('Physician Guided Package');
          break;
        default:
          break;
      }
    }
  }, [products, chartIsFetched]);

  // REQUIRE CONFIRMATION ON PACKAGE CHANGE
  React.useEffect(() => {
    setProductConfirmed(false);
  }, [selected]);

  const handleConfirm = async () => {
    setProductConfirmed(true);

    const nextPage = await handleProductBasedNavUpdate({ selectedProduct: selected, patientInfo: patientInfo });
    setNextPageURL(nextPage);
    handleProductUpdate();

    if (chart.PAYMENT_INFO?.alreadyPaid) {
      const awaitedUrl = await getNextPage({
        currentPage: 'select',
        chart: chart,
        patientInfo: patientInfo,
      });
      setNextPageURL(awaitedUrl);
    }
  };

  const handleProductUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          PRODUCT_INFO: {
            returning: true,
            selectedProduct: selected,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error); // CAPTURE THE ERROR
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  const handleSelection = (name: string) => {
    switch (name) {
      case 'Self Guided Package':
        setSelected('Self Guided Package');
        break;
      case 'Vital Package':
        setSelected('Vital Package');
        break;
      case 'Physician Guided Package':
        setSelected('Physician Guided Package');
        break;
      default:
        break;
    }
  };

  if (chartQuery.isLoading || patientDocQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Attempting to load current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && products?.length > 0) {
    return (
      <>
        <div id="product-selection-page" className="flex w-full flex-col items-center justify-center">
          <Heading id="product-selection-page-header" textContent="Product Selection Page" />
          <div id="product-cards-container" className="flex w-full flex-row flex-wrap justify-around">
            {products.map((product: Product) =>
              product.active ? (
                <button
                  type="button"
                  key={product.name}
                  onClick={() => {
                    handleSelection(product.name);
                  }}
                >
                  <ProductCard product={product} selected={selected} />
                </button>
              ) : null,
            )}
          </div>
          {patientInfo.isBulk && (
            <div className="my-4 flex flex-col items-center justify-center">
              <p className="text-2xl font-bold">
                *This price may be shared or covered by a partnership with your company/organization.
              </p>
            </div>
          )}
          {!productConfirmed && (
            <Button
              type="button"
              id="confirm-product"
              onClick={handleConfirm}
              textContent="Confirm Product Selection"
              disabled={!selected}
            />
          )}
          {productConfirmed && (
            <div id="nav" className="flex flex-row">
              {nextPageURL && <NavForward to={nextPageURL} disabled={!productConfirmed} />}
            </div>
          )}
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
